import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { getTripById, getMergedTripById, getActiveTripDataById } from '../../services/TripsService';
import { TripDetails, ActiveTripData } from '../../types/Trips';
import { FeaturesManagement } from '../../../featuresControl';
import useGetTripMerge from './useGetTripMerge';
import { getActiveTripByIdParams } from './activeTripByIdParams';

function useGetBaseTripById(
  id: string,
  fleetId: string,
  refetch: boolean,
  enabled: boolean,
): UseQueryResult<TripDetails> {
  return useQuery(
    ['trip', id, fleetId],
    async (): Promise<TripDetails> => getTripById(id, fleetId),
    {
      retry: 1,
      retryDelay: 3000,
      keepPreviousData: true,
      refetchInterval: (data) => {
        if (!refetch) return false;
        if (data?.endAt) return false;
        return FeaturesManagement.productivity.refetchInterval;
      },
      enabled: !!id && enabled,
    },
  );
}

function useGetBaseMergedTripById(
  id: string,
  fleetId: string,
  refetch: boolean,
  enabled: boolean,
): UseQueryResult<TripDetails> {
  return useQuery(
    ['merged-trip', id, fleetId],
    async (): Promise<TripDetails> => getMergedTripById(id, fleetId),
    {
      retry: 1,
      retryDelay: 3000,
      keepPreviousData: true,
      refetchInterval: (data) => {
        if (!refetch) return false;
        if (data?.endAt) return false;
        return FeaturesManagement.productivity.refetchInterval;
      },
      enabled: !!id && enabled,
    },
  );
}

function useGetActiveTripDataById(
  tripData: TripDetails | undefined,
  fallbackId: string,
  fleetId: string,
  refetch: boolean,
  enabled: boolean,
): UseQueryResult<ActiveTripData> {
  const { data: tripMerge } = useGetTripMerge(fleetId, enabled);
  const { tripId, showStandaloneAndMergedTrips } = getActiveTripByIdParams(
    tripData,
    !!tripMerge?.clientTripMergeEnabled,
    fallbackId,
  );

  return useQuery(
    ['active-trip', tripId, fleetId, showStandaloneAndMergedTrips],
    async (): Promise<ActiveTripData> => getActiveTripDataById(
      tripId,
      fleetId,
      showStandaloneAndMergedTrips,
    ),
    {
      retry: 1,
      retryDelay: 3000,
      keepPreviousData: true,
      refetchInterval: refetch ? FeaturesManagement.productivity.refetchInterval : false,
      enabled: !!tripId && enabled,
    },
  );
}

function useGetTripById(
  id: string,
  fleetId: string,
  refetch: boolean = true,
  isMergedTripHint: boolean = false,
): any {
  const regularQuery = useGetBaseTripById(id, fleetId, refetch, !isMergedTripHint);
  const shouldFetchMerged = isMergedTripHint || regularQuery.isError;
  const mergedQuery = useGetBaseMergedTripById(id, fleetId, refetch, shouldFetchMerged);

  const useMergedResult = isMergedTripHint || (regularQuery.isError && !!mergedQuery.data);
  const tripData = useMergedResult ? mergedQuery.data : regularQuery.data;
  const isMergedTrip = !!(useMergedResult || tripData?.isMergedTrip);

  const tripDataLoading = isMergedTripHint
    ? mergedQuery.isLoading
    : (regularQuery.isLoading || (regularQuery.isError && mergedQuery.isLoading));

  const tripDataIsFetching = useMergedResult ? mergedQuery.isFetching : regularQuery.isFetching;

  let resolvedError = null;
  if (isMergedTripHint) {
    resolvedError = mergedQuery.error;
  } else if (regularQuery.isError && !mergedQuery.isLoading && !mergedQuery.data) {
    resolvedError = mergedQuery.error || regularQuery.error;
  }

  const shouldPollActive = refetch && !!tripData && !tripData.endAt;

  const {
    data: activeTripData,
    isLoading: activeTripLoading,
    isFetching: activeTripIsFetching,
  } = useGetActiveTripDataById(
    tripData,
    id,
    fleetId,
    shouldPollActive,
    shouldPollActive,
  );

  const merged = tripData ? {
    ...tripData,
    distanceInKm: tripData.endAt
      ? tripData.distanceInKm
      : activeTripData?.distanceInKm || tripData.distanceInKm,
    speedMs: tripData.endAt ? null : activeTripData?.speedMs,
  } : undefined;

  const isLoading = tripDataLoading || (shouldPollActive && activeTripLoading);

  return {
    data: isLoading ? undefined : merged,
    isLoading,
    isFetching: tripDataIsFetching || activeTripIsFetching,
    error: resolvedError,
    isMergedTrip,
  };
}

export default useGetTripById;
